import React, { useEffect, useState, useRef } from 'react';

/**
 * HOC Personalizado de WidthProvider.
 * 
 * ¿Por qué este componente y no el oficial?
 * En entornos con Vite > 4.x (especialmente Vite 8 como indica el PRD), 
 * el `WidthProvider` exportado por `react-grid-layout` usa lifecycle hooks de clases antiguas 
 * y refs incompatibles que crashean en React 18+ strict mode.
 * 
 * Este HOC soluciona el problema usando el moderno `ResizeObserver` API,
 * garantizando que el Layout funcione sin fugas de memoria y empaquetado fluidamente.
 */

interface WidthProviderProps {
  measureBeforeMount?: boolean;
  [key: string]: any;
}

export function withResponsiveWidth<P extends { width: number }>(
  WrappedComponent: React.ComponentType<P>
) {
  return function ResponsiveGrid(props: Omit<P, 'width'> & WidthProviderProps) {
    const { measureBeforeMount = false, ...rest } = props;
    const [width, setWidth] = useState<number>(0);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      // Usar ResizeObserver para mayor rendimiento y cero crasheos
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const newWidth = entry.contentRect.width;
          if (newWidth > 0 && newWidth !== width) {
            // Un pequeño rAF previene el error "ResizeObserver loop limit exceeded" en UI pesadas
            window.requestAnimationFrame(() => {
              setWidth(newWidth);
            });
          }
        }
      });

      resizeObserver.observe(container);

      // Limpieza (Vital para la regla de cero memory leaks BMAD6)
      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    // Si requiere medida antes de montar, no renderizamos el componente hasta no tener ancho
    if (measureBeforeMount && width === 0) {
      return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />;
    }

    return (
      <div ref={containerRef} style={{ width: '100%', minHeight: '100%' }}>
        <WrappedComponent {...(rest as P)} width={width || 1200} />
      </div>
    );
  };
}
